/* PhoolKalaa — homepage behaviors
   Idempotent: safe across Shopify section reloads in the Theme Editor. */

(function () {
  'use strict';

  function initFadeIn(root) {
    var sections = root.querySelectorAll('.pk-fade-in');
    if (!sections.length) return;
    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (s) { s.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    sections.forEach(function (s) { observer.observe(s); });
  }

  function initHeader() {
    var header = document.querySelector('.pk-header');
    if (!header || header.dataset.pkInitialized) return;
    header.dataset.pkInitialized = 'true';

    var announcement = document.querySelector('.pk-announcement');

    var onScroll = function () {
      // Keep the header docked directly beneath the announcement bar as it
      // scrolls away, so it reaches top:0 with no gap once the bar is gone.
      var annH = announcement ? announcement.offsetHeight : 0;
      header.style.top = Math.max(0, annH - window.scrollY) + 'px';

      if (window.scrollY > 50) header.classList.add('is-shrunk');
      else header.classList.remove('is-shrunk');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }

  function initDrawer() {
    var drawer = document.querySelector('.pk-drawer');
    if (!drawer || drawer.dataset.pkInitialized) return;
    drawer.dataset.pkInitialized = 'true';

    var opener = document.querySelector('[data-pk-drawer-open]');
    var closers = drawer.querySelectorAll('[data-pk-drawer-close]');
    var firstLink = drawer.querySelector('.pk-drawer__link');

    function open() {
      drawer.hidden = false;
      // Force reflow so the transform transition can play
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.documentElement.classList.add('pk-drawer-open');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      if (firstLink) firstLink.focus({ preventScroll: true });
    }

    function close() {
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('pk-drawer-open');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      // Wait for the transform transition to finish before hiding
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 280);
      if (opener) opener.focus({ preventScroll: true });
    }

    if (opener) opener.addEventListener('click', open);
    closers.forEach(function (c) { c.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* Continuous left-to-right marquee carousel for mobile.
     - Clones every child once at init so the loop can be seamless.
     - Drives scrollLeft via requestAnimationFrame at a fixed px-per-frame rate.
     - When scrollLeft >= the first clone's offsetLeft, subtracts that amount
       to wrap; the items at the wrap point are visually identical to the
       originals, so there is no jump.
     - Pauses briefly on touch / pointer-down, and while offscreen.
     - Respects prefers-reduced-motion.
     - Idempotent (dataset guard). */
  function initAutoScroll(root) {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var carousels = root.querySelectorAll('[data-pk-autoscroll]');
    carousels.forEach(function (el) {
      if (el.dataset.pkAutoscrollInit) return;
      el.dataset.pkAutoscrollInit = 'true';

      var originalChildren = Array.from(el.children);
      if (originalChildren.length === 0) return;

      // Speed in pixels per frame (~60 fps). Override via data-pk-autoscroll-speed.
      var speed = parseFloat(el.dataset.pkAutoscrollSpeed);
      if (isNaN(speed) || speed <= 0) speed = 0.5;

      // Clone children once for seamless looping; mark clones as decorative.
      originalChildren.forEach(function (c) {
        var clone = c.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input, [tabindex]').forEach(function (f) {
          f.setAttribute('tabindex', '-1');
        });
        if (clone.matches && (clone.matches('a, button, input'))) {
          clone.setAttribute('tabindex', '-1');
        }
        el.appendChild(clone);
      });

      // Snap and smooth-scroll would fight rAF; disable here only.
      el.style.scrollSnapType = 'none';
      el.style.scrollBehavior = 'auto';

      // Wrap-around point = the first clone's left offset within the strip.
      var loopPoint = 0;
      function updateLoopPoint() {
        var firstClone = el.children[originalChildren.length];
        loopPoint = firstClone ? firstClone.offsetLeft : 0;
      }
      updateLoopPoint();
      window.addEventListener('resize', updateLoopPoint, { passive: true });

      // Pause state
      var pausedByTouch = false;
      var pausedOffscreen = false;
      var pauseTimer = null;
      function pauseTemp() {
        pausedByTouch = true;
        if (pauseTimer) clearTimeout(pauseTimer);
        pauseTimer = setTimeout(function () { pausedByTouch = false; }, 3500);
      }
      el.addEventListener('touchstart', pauseTemp, { passive: true });
      el.addEventListener('pointerdown', pauseTemp, { passive: true });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            pausedOffscreen = !entry.isIntersecting;
          });
        }, { threshold: 0.05 });
        io.observe(el);
      }

      function tick() {
        if (!pausedByTouch && !pausedOffscreen && loopPoint > 0) {
          el.scrollLeft += speed;
          if (el.scrollLeft >= loopPoint) {
            el.scrollLeft -= loopPoint;
          }
        }
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }

  /* Device-local wishlist. Product data is captured from the shared card at
     save time, so it works for guests without requiring a customer account. */
  var WISHLIST_KEY = 'phoolkala:wishlist:v1';

  function readWishlist() {
    try {
      var saved = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(saved) ? saved.filter(function (item) { return item && item.key; }) : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (error) {
      return;
    }
    document.dispatchEvent(new CustomEvent('pk:wishlist:change'));
  }

  function wishlistItemFromButton(button) {
    return {
      key: button.dataset.productKey || button.dataset.productUrl,
      handle: button.dataset.productHandle || '',
      url: button.dataset.productUrl || '#',
      title: button.dataset.productTitle || '',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || ''
    };
  }

  function syncWishlistUI(root) {
    var items = readWishlist();
    var keys = items.map(function (item) { return item.key; });
    (root || document).querySelectorAll('[data-pk-wishlist-toggle]').forEach(function (button) {
      var active = keys.indexOf(button.dataset.productKey) !== -1;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', (active ? 'Remove ' : 'Add ') + (button.dataset.productTitle || 'product') + (active ? ' from wishlist' : ' to wishlist'));
    });
    document.querySelectorAll('[data-pk-wishlist-count]').forEach(function (badge) {
      badge.dataset.pkWishlistCount = String(items.length);
      badge.setAttribute('data-count', String(items.length));
    });
  }

  function makeWishlistCard(item, viewLabel, removeLabel) {
    var card = document.createElement('article');
    card.className = 'pk-product-card pk-wishlist-card';

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'pk-product-card__wishlist is-active';
    remove.setAttribute('aria-label', removeLabel + ': ' + item.title);
    remove.setAttribute('aria-pressed', 'true');
    remove.setAttribute('data-pk-wishlist-toggle', '');
    remove.dataset.productKey = item.key;
    remove.dataset.productHandle = item.handle || '';
    remove.dataset.productUrl = item.url;
    remove.dataset.productTitle = item.title;
    remove.dataset.productImage = item.image || '';
    remove.dataset.productPrice = item.price || '';
    var heart = document.createElement('span');
    heart.className = 'pk-icon';
    heart.setAttribute('aria-hidden', 'true');
    heart.textContent = 'favorite';
    remove.appendChild(heart);
    card.appendChild(remove);

    var imageLink = document.createElement('a');
    imageLink.className = 'pk-product-card__image-wrap';
    imageLink.href = item.url;
    imageLink.setAttribute('aria-label', item.title);
    if (item.image) {
      var image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 800;
      image.height = 800;
      imageLink.appendChild(image);
    }
    card.appendChild(imageLink);

    var body = document.createElement('div');
    body.className = 'pk-product-card__body';
    var title = document.createElement('h2');
    title.className = 'pk-product-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = item.url;
    titleLink.textContent = item.title;
    title.appendChild(titleLink);
    body.appendChild(title);
    if (item.price) {
      var price = document.createElement('p');
      price.className = 'pk-product-card__price';
      price.textContent = item.price;
      body.appendChild(price);
    }
    var view = document.createElement('a');
    view.className = 'pk-product-card__cta';
    view.href = item.url;
    view.textContent = viewLabel;
    body.appendChild(view);
    card.appendChild(body);
    return card;
  }

  function renderWishlistSections() {
    var items = readWishlist();
    document.querySelectorAll('[data-pk-wishlist-grid]').forEach(function (grid) {
      var empty = grid.parentElement.querySelector('[data-pk-wishlist-empty]');
      grid.replaceChildren();
      if (!items.length) {
        grid.hidden = true;
        if (empty) empty.hidden = false;
        return;
      }
      grid.hidden = false;
      if (empty) empty.hidden = true;
      var viewLabel = grid.dataset.viewLabel || 'View Product';
      var removeLabel = grid.dataset.removeLabel || 'Remove from wishlist';
      items.forEach(function (item) {
        grid.appendChild(makeWishlistCard(item, viewLabel, removeLabel));
      });
    });
  }

  function initWishlist() {
    if (!document.documentElement.dataset.pkWishlistInitialized) {
      document.documentElement.dataset.pkWishlistInitialized = 'true';
      document.addEventListener('click', function (event) {
        var button = event.target.closest('[data-pk-wishlist-toggle]');
        if (!button) return;
        event.preventDefault();
        var item = wishlistItemFromButton(button);
        var items = readWishlist();
        var index = items.findIndex(function (saved) { return saved.key === item.key; });
        if (index === -1) items.push(item);
        else items.splice(index, 1);
        writeWishlist(items);
      });
      document.addEventListener('pk:wishlist:change', function () {
        syncWishlistUI(document);
        renderWishlistSections();
      });
      window.addEventListener('storage', function (event) {
        if (event.key === WISHLIST_KEY) {
          syncWishlistUI(document);
          renderWishlistSections();
        }
      });
    }
    syncWishlistUI(document);
    renderWishlistSections();
  }

  function initMobileCartCount() {
    if (window.pkMobileCartCountInitialized) return;
    window.pkMobileCartCountInitialized = true;

    function updateCount(count) {
      if (typeof count !== 'number') return;
      document.querySelectorAll('[data-pk-cart-count], .pk-header__cart-count').forEach(function (badge) {
        badge.dataset.count = String(count);
        if (badge.hasAttribute('data-pk-cart-count')) {
          badge.setAttribute('data-pk-cart-count', String(count));
        }
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cartData = event && event.cartData;
        if (cartData && typeof cartData.item_count === 'number') {
          updateCount(cartData.item_count);
          return;
        }

        fetch(window.routes.cart_url + '.js')
          .then(function (response) { return response.json(); })
          .then(function (cart) { updateCount(cart.item_count); })
          .catch(function () {});
      });
    }
  }

  /* Equalize product-card titles per row: titles on the same row match the
     tallest one, so price + CTA line up. When every title fits on one line,
     they all stay one line (no forced blank line). */
  function equalizeCardTitles() {
    var grids = document.querySelectorAll('.pk-products-grid');
    grids.forEach(function (grid) {
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.pk-product-card'));
      if (!cards.length) return;
      var titles = cards.map(function (c) { return c.querySelector('.pk-product-card__title'); });

      // Reset first so we measure natural (unforced) heights.
      titles.forEach(function (t) { if (t) t.style.minHeight = ''; });

      // Group titles by the card's row (cards in one row share the same top).
      var rows = [];
      cards.forEach(function (card, i) {
        var t = titles[i];
        if (!t) return;
        var top = Math.round(card.getBoundingClientRect().top);
        var row = null;
        for (var r = 0; r < rows.length; r++) {
          if (Math.abs(rows[r].top - top) < 8) { row = rows[r]; break; }
        }
        if (!row) { row = { top: top, items: [] }; rows.push(row); }
        row.items.push(t);
      });

      rows.forEach(function (row) {
        var max = 0;
        row.items.forEach(function (t) { if (t.offsetHeight > max) max = t.offsetHeight; });
        row.items.forEach(function (t) { t.style.minHeight = max + 'px'; });
      });
    });
  }

  var equalizeRaf;
  function scheduleEqualize() {
    if (equalizeRaf) cancelAnimationFrame(equalizeRaf);
    equalizeRaf = requestAnimationFrame(equalizeCardTitles);
  }

  function init() {
    document.documentElement.classList.add('pk-js-ready');
    initHeader();
    initDrawer();
    initFadeIn(document);
    initAutoScroll(document);
    initWishlist();
    initMobileCartCount();
    equalizeCardTitles();
    window.addEventListener('resize', scheduleEqualize);
    // Re-run once the display font loads (Bodoni changes title height).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleEqualize);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor section reload support
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      initHeader();
      initDrawer();
      initFadeIn(event.target);
      initAutoScroll(event.target);
      initWishlist();
      scheduleEqualize();
    }
  });
})();
